import { ForwardedRef, useImperativeHandle, useRef } from 'react';

import { ActionInputHandle } from '@bonusly/common-action-input';
import { usePeerToPeerModules, useSharedConfig } from '@bonusly/common-give-box';
import { useAllowanceBoostId } from '@bonusly/common-rewards';

import { Bonus } from '../../../../types/bonus';
import { useBudget } from '../components/comments/hooks/use-budget';
import { useInitialState } from '../components/comments/hooks/use-initial-state';
import { useEditRecognition } from '../hooks/use-edit-recognition';
import { useValidator } from '../hooks/use-validator';
import { parseErrorMessage } from '../utils/error';
import { BonusFeedTrackedAction, useTrackBonusFeedItemEvent } from './use-bonus-feed-item-analytics';

interface UseEditBonusOptions {
	bonus: Bonus;
	onClose: VoidFunction;
	onRequestClose: VoidFunction;
	onSuccess?: VoidFunction;
	ref?: ForwardedRef<{ requestClose: VoidFunction }>;
}

export const useEditModal = ({ bonus, onClose, onRequestClose, onSuccess, ref }: UseEditBonusOptions) => {
	const inputRef = useRef<ActionInputHandle>(null);
	const { id: allowanceBoostId } = useAllowanceBoostId();
	const initialState = useInitialState(bonus);
	const handlers = useSharedConfig({ includeGroups: true });
	const modules = usePeerToPeerModules();
	const trackEdit = useTrackBonusFeedItemEvent(bonus, BonusFeedTrackedAction.BonusEdit);

	// Create props to pass into the ActionInput
	const budget = useBudget();
	const totalAmount = bonus.amount * bonus.receivers.length;
	const validator = useValidator({ balanceAdjustment: totalAmount });

	const { isSubmitting, onSubmit } = useEditRecognition(bonus, {
		onError: (err: unknown) => {
			const message = parseErrorMessage(err);
			inputRef.current?.error(message);
		},
		onSuccess: () => {
			onSuccess?.();
			trackEdit();
		},
	});

	const handleClose = () => {
		const data = inputRef.current?.data();

		if (data?.text !== bonus.reason) {
			onRequestClose();
		} else {
			onClose();
		}
	};

	useImperativeHandle(ref, () => ({
		requestClose: handleClose,
	}));

	const config = {
		allowanceBoostId,
		budget,
		context: 'recognition',
		isSubmitting,
		modules,
		onSubmit,
		ref: inputRef,
		validator,
		...handlers,
		...initialState,
	};

	return { config, handleClose };
};
